// Filename: src/screens/import/UploadedImport.jsx
// Last Updated: 2025-02-03 19:03:32 UTC
// Author: raghavkunkee

import { Badge } from "primereact/badge";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Toast } from "primereact/toast";
import { useCallback, useEffect, useRef, useState, useContext } from "react";
import { subscriberContext } from "../../App";
import moment from "moment";

import {
  loading as Loading,
  loadingdrill as LoadingDrill,
} from "../../components/generic/loading";
import {
  StyledLabelBlack,
  PanelHeading,
} from "../../components/routing/Styled";
import { StageLabel } from "../../containers/Styled";
import Pagination from "../books/Pagination";
import { fetchWithFallback, reportFieldGroups } from "../../utils/apiUtils";

/**
 * Component for displaying all imported files
 * @param {Object} props - Component props
 * @param {boolean} props.load - Trigger reload of data
 * @return {JSX.Element} Rendered component
 */
const ALLImportedFiles = ({ load }) => {
  const { keycloak } = useContext(subscriberContext);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [lastPage, setLastPage] = useState(1);
  const [pageErr, setPageErr] = useState(1);
  const [sizeErr, setSizeErr] = useState(5000);
  const [lastPageErr, setLastPageErr] = useState(1);
  const [selectedRow, setSelectedRow] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);
  const [importData, setImportData] = useState([]);
  const [drilldownData, setDrilldownData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingDrilldown, setLoadingDrilldown] = useState(false);
  const [error, setError] = useState(null);
  const toast = useRef(null);

  const sizeList = [
    { name: 20, value: 20 },
    { name: 30, value: 30 },
    { name: 40, value: 40 },
  ];

  const sizeListErr = [
    { name: 1000, value: 1000 },
    { name: 5000, value: 5000 },
    { name: 10000, value: 10000 },
    { name: 50000, value: 50000 },
    { name: 100000, value: 100000 },
    { name: 500000, value: 500000 },
  ];

  const fetchImportData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const url = `${process.env.REACT_APP_APIIMPORTURL}/v1/requests/import?page=${page - 1}&size=${size}`;
      const data = await fetchWithFallback(
        url,
        { _embedded: { responseVOList: [] }, page: { totalPages: 1 } },
        {},
        keycloak?.token
      );

      setLastPage(data?.page?.totalPages || 1);
      setImportData(data._embedded?.responseVOList || []);
    } catch (error) {
      console.error("Error fetching import data:", error);
      setError("Failed to load import data");
    } finally {
      setLoading(false);
    }
  }, [page, size, keycloak?.token]);

  const fetchDrilldownData = useCallback(async () => {
    if (!selectedRow?.id) return;

    try {
      setLoadingDrilldown(true);
      const url = `${process.env.REACT_APP_APIIMPORTURL}/v1/requests/${selectedRow.id}/items/error?page=${pageErr - 1}&size=${sizeErr}`;
      const data = await fetchWithFallback(
        url,
        { _embedded: { importRequestItemVOList: [] }, page: { totalPages: 1 } },
        {},
        keycloak?.token
      );

      setLastPageErr(data?.page?.totalPages || 1);
      setDrilldownData(data._embedded?.importRequestItemVOList || []);
    } catch (error) {
      console.error("Error fetching drilldown data:", error);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load error details",
        life: 3000,
      });
    } finally {
      setLoadingDrilldown(false);
    }
  }, [selectedRow?.id, pageErr, sizeErr, keycloak?.token]);

  useEffect(() => {
    setSelectedRow(null);
  }, [load]);

  useEffect(() => {
    fetchImportData();
  }, [fetchImportData, load]);

  useEffect(() => {
    fetchDrilldownData();
  }, [fetchDrilldownData]);

  // Table cell renderers
  const fileNameBody = useCallback((rowData) => (
    <div>
      {(rowData?.stats?.val?.successCount > 0 ||
        rowData?.stats?.val?.failureCount > 0 ||
        rowData?.stats?.fetch?.failureCount > 0 ||
        rowData?.stats?.sync?.failureCount > 0) && (
        <button
          className="p-link p-datatable-row-toggler"
          onClick={() => handleRowToggle(rowData)}
        >
          <i
            className={`pi ${
              expandedRows[rowData.id] ? "pi-chevron-down" : "pi-chevron-right"
            }`}
          />
        </button>
      )}
      <span>{rowData?.requestFile?.originalFileName}</span>
    </div>
  ), [expandedRows]);

  const totalRecordsBody = useCallback((rowData) => (
    <Badge value={rowData?.stats?.totalCount || 0} severity="info" />
  ), []);

  const successCountBody = useCallback((rowData) => (
    <Badge
      value={rowData?.stats?.sync?.successCount || 0}
      severity="success"
    />
  ), []);

  const failCountBody = useCallback((rowData) => {
    const failcount =
      (rowData?.stats?.val?.failureCount || 0) +
      (rowData?.stats?.fetch?.failureCount || 0) +
      (rowData?.stats?.sync?.failureCount || 0);
    return <Badge value={failcount} severity="danger" />;
  }, []);

  const processingStatusBody = useCallback((rowData) => (
    <StageLabel className={`stage-badge status-${rowData?.processingStatus}`}>
      {rowData.processingStatus}
    </StageLabel>
  ), []);

  const requestTypeBody = useCallback((rowData) => {
    const types = {
      REFRESH_DATA: "Fetch",
      GENERATE_REPORT: "Generate Report",
      REFRESH_DATA_AND_GENERATE_REPORT: "Fetch & Generate Report"
    };
    return (
      <span>
        {types[rowData.requestType] || rowData.requestType} ({rowData.marketplace})
      </span>
    );
  }, []);

  const fieldGroupBody = useCallback((rowData) => (
    <span>{reportFieldGroups[rowData.fieldGroup] || rowData.fieldGroup}</span>
  ), []);

  /*
  // Add new function to format the date
  const formatDateTime = useCallback((dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString()
      .replace('T', ' ')
      .replace(/\.\d{3}Z$/, '');
  }, []);
  // Add submit time body renderer
  const submitTimeBody = useCallback((rowData) => (
    <span>{formatDateTime(rowData?.submitTime)}</span>
  ), [formatDateTime]);
  */
  // Simplified submit time body renderer using moment.js
  const submitTimeBody = useCallback((rowData) => (
    <div>
      <span>{moment(rowData?.submitTime).format("YYYY-MM-DD HH:mm:ss")}</span>
    </div>
  ), []);
  // Main table columns
  const columns = [
    {
      field: "requestFile.originalFileName",
      header: "File Name",
      expander: true,
      body: fileNameBody,
    },
    {
      field: "requestFile.requestType",
      header: "Request Type",
      body: requestTypeBody,
    },
    {
      field: "requestFile.fieldGroup",
      header: "Fields",
      body: fieldGroupBody,
    },
    {
      field: "submitTime",
      header: "Submit Time",
      body: submitTimeBody,
      // sortable: true,
    },
    {
      field: "stats.totalCount",
      header: "Total Records",
      body: totalRecordsBody,
    },
    {
      field: "stats.val.successCount",
      header: "Success Count",
      body: successCountBody,
    },
    {
      field: "stats.val.failureCount",
      header: "Fail Count",
      body: failCountBody,
    },
    {
      field: "processingStatus",
      header: "Processing Status",
      body: processingStatusBody,
    },
  ];

  // Drilldown table columns
  const drilledColumns = [
    { field: "type", header: "Type" },
    { field: "id", header: "ID" },
    { field: "marketplace", header: "Market Place" },
    { field: "remarks", header: "Remarks" },
  ];
/*
  const handleRowToggle = useCallback((rowData) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowData.id]: !prev[rowData.id]
    }));
  }, []);
*/
  // Modified handleRowToggle function
  const handleRowToggle = useCallback((rowData) => {
    setExpandedRows(prev => {
      const newExpandedRows = { ...prev };
      if (newExpandedRows[rowData.id]) {
        delete newExpandedRows[rowData.id]; // Remove the entry if it exists (collapse)
      } else {
        newExpandedRows[rowData.id] = true; // Add the entry if it doesn't exist (expand)
      }
      return newExpandedRows;
    });
  }, []);

  const onRowClick = useCallback((event) => {
    const failcount =
      (event.data.stats?.val?.failureCount || 0) +
      (event.data.stats?.fetch?.failureCount || 0) +
      (event.data.stats?.sync?.failureCount || 0);

    if (failcount) {
      setSelectedRow(event.data);
    } else {
      toast.current.show({
        severity: "info",
        summary: "No Failed Records",
        detail: "There are no failed records to display",
        life: 3000,
      });
    }
  }, []);

  const expandableBodyTemplate = useCallback((rowData) => {
    if (!rowData.stats) return null;

    return (
      <div className="flex justify-content-between align-items-center" style={{ padding: "0 5rem" }}>
        {[
          { label: "Validated Success", count: rowData?.stats?.val?.successCount || 0, severity: "success" },
          { label: "Validated Fail", count: rowData?.stats?.val?.failureCount || 0, severity: "danger" },
          { label: "Fetch Success", count: rowData?.stats?.fetch?.successCount || 0, severity: "success" },
          { label: "Fetch Fail", count: rowData?.stats?.fetch?.failureCount || 0, severity: "danger" },
          { label: "Sync Success", count: rowData?.stats?.sync?.successCount || 0, severity: "success" },
          { label: "Sync Fail", count: rowData?.stats?.sync?.failureCount || 0, severity: "danger" },
        ].map(({ label, count, severity }) => (
          <div key={label}>
            <StyledLabelBlack>{label}:</StyledLabelBlack>
            <Badge value={count} severity={severity} className="ml-2" />
          </div>
        ))}
      </div>
    );
  }, []);

  return (
    <div>
      <Toast ref={toast} />
      
      {error && (
        <div className="p-message p-message-error">
          <i className="pi pi-exclamation-triangle"></i>
          <span className="p-message-text">{error}</span>
        </div>
      )}

      {!loading && (
        <>
          <DataTable
            value={importData}
            rowExpansionTemplate={expandableBodyTemplate}
            expandedRows={expandedRows}
            onRowToggle={(e) => handleRowToggle(e.data)}
            dataKey="id"
            rows={15}
            selectionMode="single"
            selection={selectedRow}
            onSelectionChange={(e) => setSelectedRow(e.value)}
            onRowClick={onRowClick}
          >
            {columns.map((col) => (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                expander={col.expander}
                body={col.body}
                sortable
              />
            ))}
          </DataTable>
          
          <Pagination
            page={page}
            setPage={setPage}
            size={size}
            setSize={setSize}
            lastPage={lastPage}
            sizeList={sizeList}
          />
        </>
      )}

      {loading && <Loading />}

      {selectedRow && (selectedRow?.stats?.val?.failureCount > 0 ||
        selectedRow?.stats?.fetch?.failureCount > 0 ||
        selectedRow?.stats?.sync?.failureCount > 0) && (
        <div>
          <PanelHeading className="p-d-flex p-ai-center text-color text-base font-medium">
            <span>Failed Records Details</span>
          </PanelHeading>

          {!loadingDrilldown && (
            <>
              <DataTable
                value={drilldownData}
                dataKey="id"
                rows={5000}
              >
                {drilledColumns.map((col) => (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    sortable
                  />
                ))}
              </DataTable>

              <Pagination
                page={pageErr}
                setPage={setPageErr}
                size={sizeErr}
                setSize={setSizeErr}
                lastPage={lastPageErr}
                sizeList={sizeListErr}
              />
            </>
          )}

          {loadingDrilldown && <LoadingDrill />}
        </div>
      )}
    </div>
  );
};

export default ALLImportedFiles;